/**
 * Ayarlar on the desktop (artboard F_AyarlarMasaustu): sidebar Ayarlar with a sub-navigation
 * (Kurulum, Bağlantılar, Politika, Sert sınırlar, Ajanlar, Takvim, Tercihler, Veri ve
 * saklama, Güvenlik, Hakkında). Bağlantılar is a grouped table with filters and search; the
 * right rail is the setup order, the change log, or a connection's detail.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { Connection, ConnectionGroupId, ConnectionStatus, Settings } from '../api/settings.js';
import { usePanel } from '../app/context.js';
import { Link, type SettingsTab } from '../app/router.js';
import {
  backupMissing,
  connectionPurpose,
  DESKTOP_TABS,
  desktopTab,
  groupSummaries,
  holderLabel,
  isLive,
  killSwitchUntested,
  pendingSuggestions,
  type SetupSummary,
  STATUS_LABELS,
  setupSummary,
  statusCounts,
  TAB_TITLES,
} from '../app/settings.js';
import { ApiUsageBody, ApiUsageRail } from '../components/api-usage.js';
import { Icon } from '../components/icons.js';
import { TriangleGlyph } from '../components/primitives.js';
import {
  AuditList,
  ConnectionPill,
  HardLimits,
  Rich,
  RowAction,
  richToString,
  ServiceGlyph,
  SetupBar,
  SetupLegend,
  StatusGlyph,
} from '../components/settings.js';
import { TopBar, TopBarStatus } from '../components/shell.js';
import { formatClock } from '../lib/format.js';
import { ConnectionDetail } from './Baglanti.js';
import {
  AboutBody,
  AgentsBody,
  ChangeLogBody,
  DataBody,
  PolicyBody,
  PreferencesBody,
  ScheduleBody,
  SecurityBody,
  SettingsError,
  SetupBody,
} from './settings-sections.js';

function TabExtra({ tab, settings, summary }: { tab: SettingsTab; settings: Settings; summary: SetupSummary }) {
  switch (tab) {
    case 'kurulum':
      return (
        <span className="st-tabs__count mono">
          {summary.ready}/{summary.total}
        </span>
      );
    case 'baglantilar':
      return <span className="st-tabs__count st-tabs__count--ink2 mono">{settings.connections.length}</span>;
    case 'politika': {
      const count = pendingSuggestions(settings).length;
      return count > 0 ? <span className="st-tabs__badge mono">{count}</span> : null;
    }
    case 'sert-sinirlar':
      return <Icon name="lock" size={12} />;
    case 'ajanlar':
      return <span className="st-tabs__count mono">{settings.agents.length}</span>;
    case 'takvim':
      return <span className="st-tabs__count mono">{settings.schedule.jobs.length}</span>;
    case 'veri':
      return backupMissing(settings) ? (
        <span className="danger">
          <TriangleGlyph />
        </span>
      ) : null;
    case 'guvenlik':
      return killSwitchUntested(settings) ? (
        <span className="warning">
          <TriangleGlyph />
        </span>
      ) : null;
    case 'hakkinda':
      return <span className="st-tabs__count mono">{settings.about.version}</span>;
    default:
      return null;
  }
}

function tabAria(tab: SettingsTab, settings: Settings): string | undefined {
  switch (tab) {
    case 'politika': {
      const count = pendingSuggestions(settings).length;
      return count > 0 ? `Politika, ${count} öneri kararını bekliyor` : undefined;
    }
    case 'sert-sinirlar':
      return 'Sert sınırlar, kodda kilitli';
    case 'veri':
      return backupMissing(settings) ? 'Veri ve saklama: yedek yok' : undefined;
    case 'guvenlik':
      return killSwitchUntested(settings) ? 'Güvenlik: acil durdurma denenmedi' : undefined;
    default:
      return undefined;
  }
}

/**
 * Between 1024 and 1279 px the tab strip is wider than the page (audit 25 Oct 2026, F15). It scrolls
 * sideways; these flags fade the edge that hides tabs, and the current tab is scrolled into view.
 */
function useTabStripEdges(current: SettingsTab | null) {
  const ref = useRef<HTMLElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const update = () => {
    const el = ref.current;
    if (!el) return;
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((old) => (old.start === start && old.end === end ? old : { start, end }));
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the current tab changes.
  useEffect(() => {
    const el = ref.current;
    const on = el?.querySelector<HTMLElement>('[aria-current="page"]');
    if (el && on && typeof on.scrollIntoView === 'function' && el.scrollWidth > el.clientWidth) {
      on.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [current]);
  return { ref, update, ...edges };
}

function SettingsTabs({
  current,
  settings,
  summary,
}: {
  current: SettingsTab | null;
  settings: Settings;
  summary: SetupSummary;
}) {
  const strip = useTabStripEdges(current);
  return (
    <nav
      ref={strip.ref}
      className="st-tabs"
      aria-label="Ayar bölümleri"
      data-more-start={strip.start ? '' : undefined}
      data-more-end={strip.end ? '' : undefined}
      onScroll={strip.update}
    >
      {DESKTOP_TABS.map((tab) => (
        <Link
          key={tab}
          to={tab === 'baglantilar' ? '/ayarlar' : `/ayarlar/${tab}`}
          className={current === tab ? 'st-tabs__item st-tabs__item--on' : 'st-tabs__item'}
          aria-current={current === tab ? 'page' : undefined}
          aria-label={tabAria(tab, settings)}
        >
          {TAB_TITLES[tab]}
          <TabExtra tab={tab} settings={settings} summary={summary} />
        </Link>
      ))}
    </nav>
  );
}

type Filter = ConnectionStatus | 'all';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'Tümü' },
  { id: 'awaiting_sercan', label: 'Senden bekleniyor' },
  { id: 'applying', label: 'Başvuru sürüyor' },
  { id: 'connected', label: 'Bağlı' },
  { id: 'not_connected', label: 'Bağlı değil' },
  { id: 'disabled', label: 'Kapalı' },
];

function fold(text: string): string {
  return text.toLocaleLowerCase('tr');
}

function ServiceCell({
  connection,
  settings,
  next,
  selected,
}: {
  connection: Connection;
  settings: Settings;
  next: boolean;
  selected: boolean;
}) {
  const off = connection.status === 'disabled';
  const name = (
    <Link
      to={`/ayarlar/baglanti/${connection.id}`}
      className="ctable__name"
      aria-current={selected ? 'true' : undefined}
    >
      {connection.name}
    </Link>
  );
  if (off) {
    return (
      <td className="ctable__svc">
        <div className="ctable__svc-in">
          <ServiceGlyph connection={connection} variant="bare" />
          <span className="ctable__one ellipsis">
            {name}
            <span className="ctable__qual"> · {richToString(connection.purpose)}</span>
          </span>
        </div>
      </td>
    );
  }
  const alert = connection.alert && !isLive(connection) ? connection.alert : null;
  return (
    <td className="ctable__svc">
      <div className="ctable__svc-in">
        <ServiceGlyph connection={connection} />
        <span className="ctable__svc-text">
          <span className="ctable__l1">
            {name}
            <span className="ctable__qual">
              <Rich text={connection.desktopQualifier ?? connection.qualifier} />
            </span>
            {next ? <span className="key ctable__next">Sıradaki</span> : null}
          </span>
          {alert ? (
            <span className="ctable__l2 ctable__l2--alert">
              <TriangleGlyph />
              {alert.text}
            </span>
          ) : connection.notice ? (
            <span className="ctable__l2">
              <Icon name="info" size={12} />
              <span className="ellipsis">{connection.notice}</span>
            </span>
          ) : connection.warmup ? (
            <span className="ctable__l2">
              ısınma
              {/* biome-ignore lint/a11y/useSemanticElements: <meter> cannot be styled to the drafting look; role keeps the semantics. */}
              <span
                role="meter"
                aria-valuemin={0}
                aria-valuemax={connection.warmup.days}
                aria-valuenow={connection.warmup.day}
                aria-label={`Isınma ${connection.warmup.days} günün ${connection.warmup.day} günü`}
                className="warm-meter"
              >
                <span
                  className="warm-meter__fill"
                  style={{ width: `${(connection.warmup.day / connection.warmup.days) * 100}%` }}
                />
              </span>
              <span className="mono">
                {connection.warmup.day}/{connection.warmup.days} gün
              </span>{' '}
              · günlük tavan <span className="mono">{connection.warmup.dailyCap}</span>
            </span>
          ) : (
            <span className="ctable__l2 ctable__l2--text">
              <Rich text={connectionPurpose(settings, connection)} />
            </span>
          )}
        </span>
      </div>
    </td>
  );
}

function ConnectionRow({
  connection,
  settings,
  next,
  selected,
}: {
  connection: Connection;
  settings: Settings;
  next: boolean;
  selected: boolean;
}) {
  const off = connection.status === 'disabled';
  const alert = connection.alert !== null && !isLive(connection);
  const className = [
    'ctable__row',
    off ? 'ctable__row--off' : '',
    next ? 'ctable__row--next' : '',
    alert ? 'ctable__row--alert' : '',
    selected ? 'ctable__row--selected' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <tr className={className}>
      <ServiceCell connection={connection} settings={settings} next={next} selected={selected} />
      <td className="ctable__status">
        <ConnectionPill status={connection.status} />
        {!off && connection.statusNote ? <span className="ctable__note">{connection.statusNote}</span> : null}
      </td>
      <td
        className={connection.tokenHolder ? (off ? 'ctable__holder ink-2' : 'ctable__holder') : 'ctable__holder ink-3'}
      >
        {holderLabel(connection.tokenHolder)}
      </td>
      <td className={connection.scopeSummary === '-' ? 'ctable__scope ink-3' : 'ctable__scope'}>
        {connection.scopeSummary}
      </td>
      <td className={isLive(connection) ? 'ctable__check mono' : 'ctable__check mono ink-2'}>
        {connection.lastCheckAt ? formatClock(connection.lastCheckAt) : <span className="ink-3">-</span>}
      </td>
      <td className="ctable__action">
        <RowAction connection={connection} primary={next} small={off} />
      </td>
    </tr>
  );
}

function ConnectionsTable({ settings, selectedId }: { settings: Settings; selectedId: string | null }) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState<ReadonlySet<ConnectionGroupId>>(new Set(['core', 'store', 'growth']));
  const summary = setupSummary(settings);
  const counts = statusCounts(settings.connections);
  const needle = fold(query.trim());
  const narrowing = filter !== 'all' || needle !== '';
  const matches = (connection: Connection) =>
    (filter === 'all' || connection.status === filter) &&
    (needle === '' ||
      fold(
        `${connection.name} ${richToString(connection.qualifier)} ${richToString(connection.purpose)} ${connection.statusNote}`,
      ).includes(needle));
  const toggle = (id: ConnectionGroupId) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpen(next);
  };
  const groups = groupSummaries(settings);
  const shown = groups.map((group) => ({ group, rows: group.connections.filter(matches) }));
  const total = shown.reduce((sum, item) => sum + item.rows.length, 0);
  return (
    <section className="panel ctable" aria-labelledby="bag-h">
      <div className="ctable__top">
        <h2 id="bag-h" className="ctable__title">
          Bağlantılar
        </h2>
        <span className="mono ink-2 ctable__count">{settings.connections.length}</span>
        <span className="ctable__hint">hepsinin sahibi Sercan · token yalnız dağıtıcı ve içe alma işçisinde</span>
        <label className="ctable__search">
          <Icon name="search" size={14} />
          <input
            type="search"
            aria-label="Bağlantı ara"
            placeholder="Bağlantı ara"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      </div>
      {/* biome-ignore lint/a11y/useSemanticElements: a toolbar of toggle buttons, not a form fieldset. */}
      <div role="group" aria-label="Duruma göre süz" className="ctable__filters">
        {FILTERS.map((item) => {
          const count = item.id === 'all' ? settings.connections.length : counts[item.id];
          const on = filter === item.id;
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={on}
              className={on ? 'fchip fchip--on' : 'fchip'}
              onClick={() => setFilter(on && item.id !== 'all' ? 'all' : item.id)}
            >
              {item.id === 'all' ? null : (
                <span className={`fchip__glyph fchip__glyph--${item.id}`}>
                  <StatusGlyph status={item.id} />
                </span>
              )}
              {item.label}
              <span
                className={item.id === 'awaiting_sercan' && !on ? 'mono fchip__count--accent' : 'mono fchip__count'}
              >
                {count}
              </span>
            </button>
          );
        })}
        <span className="ctable__legend">
          <span className="ctable__legend-item">
            <span className="warning">
              <TriangleGlyph />
            </span>
            {STATUS_LABELS.expiring} <span className="mono">{counts.expiring}</span>
          </span>
          <span className="ctable__legend-item">
            <span className="danger">
              <TriangleGlyph />
            </span>
            {STATUS_LABELS.error} <span className="mono">{counts.error}</span>
          </span>
        </span>
      </div>
      <table aria-label="Bağlantılar, gruplara göre" className="ctable__table">
        <colgroup>
          <col />
          <col className="ctable__c-status" />
          <col className="ctable__c-holder" />
          <col className="ctable__c-scope" />
          <col className="ctable__c-check" />
          <col className="ctable__c-action" />
        </colgroup>
        <thead>
          <tr className="ctable__cols">
            <th scope="col">Hizmet · ne için</th>
            <th scope="col">Durum</th>
            <th scope="col">Token kimde</th>
            <th scope="col">Yetki</th>
            <th scope="col">Kontrol</th>
            <th scope="col" className="ctable__cols-end">
              Eylem
            </th>
          </tr>
        </thead>
        {shown.map(({ group, rows }) => {
          if (narrowing && rows.length === 0) return null;
          const expanded = narrowing || open.has(group.id);
          return (
            <tbody key={group.id} className="ctable__group">
              <tr className={expanded ? 'ctable__ghead' : 'ctable__ghead ctable__ghead--closed'}>
                <th scope="rowgroup" colSpan={6} className="ctable__gcell">
                  <button
                    type="button"
                    className="ctable__gbtn"
                    aria-expanded={expanded}
                    disabled={narrowing}
                    onClick={() => toggle(group.id)}
                  >
                    <span className="key ctable__gkey">
                      {group.title} · {group.connections.length}
                    </span>
                    {!expanded ? (
                      <span className="ctable__gnames ellipsis">
                        {group.connections.map((connection) => connection.name).join(', ')}
                      </span>
                    ) : null}
                    <span className="ctable__gsum">
                      {group.parts.map((part, index) => (
                        <span key={part.text} className={`sum-part sum-part--${part.tone}`}>
                          {index > 0 ? <span className="sum-part__dot"> · </span> : null}
                          {part.triangle ? <TriangleGlyph /> : null}
                          {part.text}
                          {index === group.parts.length - 1 && group.desktopSuffix ? (
                            <span className="sum-part--plain">{group.desktopSuffix}</span>
                          ) : null}
                        </span>
                      ))}
                    </span>
                    <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={14} className="ink-3" />
                  </button>
                </th>
              </tr>
              {expanded
                ? rows.map((connection) => (
                    <ConnectionRow
                      key={connection.id}
                      connection={connection}
                      settings={settings}
                      next={summary.next?.id === connection.id}
                      selected={selectedId === connection.id}
                    />
                  ))
                : null}
            </tbody>
          );
        })}
        {narrowing && total === 0 ? (
          <tbody>
            <tr className="ctable__empty">
              <td colSpan={6}>Bu süzgeçle bağlantı yok.</td>
            </tr>
          </tbody>
        ) : null}
      </table>
    </section>
  );
}

const RAIL_STEPS = 10;

function SetupRail({ settings, summary }: { settings: Settings; summary: SetupSummary }) {
  const first = summary.steps.slice(0, RAIL_STEPS);
  const later = summary.steps.slice(RAIL_STEPS);
  return (
    <aside className="panel st-rail" aria-labelledby="kur-h">
      <div className="st-rail__head">
        <h2 id="kur-h" className="st-rail__title">
          Kurulum
        </h2>
        <span className="mono ink-2" aria-live="polite">
          {summary.ready} / {summary.total} hazır
        </span>
      </div>
      <div className="st-rail__bar drafting">
        <SetupBar summary={summary} />
        <SetupLegend summary={summary} grid />
        <p className="st-rail__note">{summary.counts.disabled} kapalı bağlantı sayılmaz; politika açınca eklenir.</p>
      </div>
      <div className="st-rail__sub">
        <h3 className="key ink-2">Kurulum sırası</h3>
        <span className="mono ink-2">ilk {first.length} adım</span>
      </div>
      <ol className="rail-steps">
        {first.map((connection, index) => {
          const isNext = summary.next?.id === connection.id;
          const alert = connection.alert && !isLive(connection) ? connection.alert : null;
          return (
            <li key={connection.id} className={isNext ? 'rail-step rail-step--next' : 'rail-step'}>
              <span className={isNext ? 'rail-step__num rail-step__num--next mono' : 'rail-step__num mono'}>
                {index + 1}
              </span>
              <span className="rail-step__text">
                <Link to={`/ayarlar/baglanti/${connection.id}`} className="rail-step__title">
                  {connection.setup?.title ?? connection.name}
                </Link>
                <span className="rail-step__hint">
                  {connection.setup ? <Rich text={connection.setup.hint} /> : null}
                </span>
              </span>
              {isNext ? (
                <Link
                  to={`/ayarlar/baglanti/${connection.id}`}
                  className="row-btn row-btn--primary"
                  aria-label={`${connection.setup?.title ?? connection.name} ile başla`}
                >
                  Başla
                </Link>
              ) : connection.status === 'applying' ? (
                <span className="rail-step__state">
                  <span className="hatch-mark" aria-hidden="true" />
                  {connection.setup?.waitingOn ?? 'başvuruda'}
                </span>
              ) : alert ? (
                <span className="rail-step__state rail-step__state--danger">
                  <TriangleGlyph />
                  {alert.short}
                </span>
              ) : (
                <span />
              )}
            </li>
          );
        })}
      </ol>
      {later.length > 0 ? (
        <div className="st-rail__later">
          <h3 className="key ink-2">Sonra · {later.length}</h3>
          <p>{later.map((connection) => connection.setup?.short ?? connection.name).join(', ')}</p>
        </div>
      ) : null}
      <HardNote settings={settings} />
    </aside>
  );
}

function HardNote({ settings }: { settings: Settings }) {
  return (
    <div className="st-rail__foot">
      <Icon name="lock" size={14} className="ink-2" />
      <p>
        <span className="st-rail__strong">Sert sınır:</span> ajanlar token tutmaz, dış gönderim yapamaz.{' '}
        <Link to="/ayarlar/sert-sinirlar">Değiştirmek için GitHub PR</Link>
        <span className="sr-only"> ({settings.repo.name})</span>.
      </p>
    </div>
  );
}

function ChangeLogRail({ settings }: { settings: Settings }) {
  return (
    <aside className="panel st-rail" aria-labelledby="log-h">
      <div className="st-rail__head">
        <h2 id="log-h" className="st-rail__title">
          Değişiklik kaydı
        </h2>
        <Link to="/ayarlar/degisiklikler" className="st-rail__link">
          tümü
        </Link>
      </div>
      <div className="st-rail__rules">
        <p>
          <span className="st-rail__strong">Sıkılaştırmak</span> tek dokunuş.
        </p>
        <p>
          <span className="st-rail__strong">Gevşetmek</span> 1,5 sn basılı tut ve gerekçe yaz; bağlantı yokken kapalı.
        </p>
        <p>Her değişiklik kim, ne zaman, gerekçe ile kayda yazılır; kayıt silinmez.</p>
      </div>
      <div className="st-rail__log">
        <AuditList entries={settings.audit.slice(0, 8)} empty="Henüz değişiklik yok." />
      </div>
      <HardNote settings={settings} />
    </aside>
  );
}

function ClosedRail({ settings }: { settings: Settings }) {
  const closed = settings.connections.filter((connection) => connection.status === 'disabled');
  return (
    <aside className="panel st-rail" aria-labelledby="kapali-h">
      <div className="st-rail__head">
        <h2 id="kapali-h" className="st-rail__title">
          Kapalı bağlantılar
        </h2>
        <span className="mono ink-2">{closed.length}</span>
      </div>
      <p className="st-rail__intro">Kuruluma sayılmaz; politika açınca sıraya eklenir.</p>
      <ul className="rail-closed">
        {closed.map((connection) => (
          <li key={connection.id} className="rail-closed__item">
            <ServiceGlyph connection={connection} variant="bare" />
            <span className="rail-closed__text">
              <Link to={`/ayarlar/baglanti/${connection.id}`} className="rail-closed__name">
                {connection.name}
              </Link>
              <span className="rail-closed__why">{connection.disabledReason}</span>
            </span>
          </li>
        ))}
      </ul>
      <HardNote settings={settings} />
    </aside>
  );
}

function DetailPane({ connection, settings, now }: { connection: Connection; settings: Settings; now: number }) {
  return (
    <aside className="panel st-pane" aria-label={`${connection.name} bağlantısı`}>
      <div className="st-pane__head">
        <span className="key ink-2">
          Bağlantı · son kontrol{' '}
          <span className="mono">{connection.lastCheckAt ? formatClock(connection.lastCheckAt) : '-'}</span>
        </span>
        <Link to="/ayarlar" className="icon-btn" aria-label="Ayrıntıyı kapat">
          <Icon name="x" size={16} />
        </Link>
      </div>
      <div className="st-pane__body">
        <ConnectionDetail connection={connection} settings={settings} now={now} pane />
      </div>
    </aside>
  );
}

function PanelHead({ id, title, hint, lock = false }: { id: string; title: string; hint: string; lock?: boolean }) {
  return (
    <div className="st-panel__head">
      <h2 id={id} className="st-panel__title">
        {title}
      </h2>
      {lock ? <Icon name="lock" size={14} className="ink-2" /> : null}
      <span className="st-panel__hint">{hint}</span>
    </div>
  );
}

const PANEL_HINTS: Partial<Record<SettingsTab, { hint: string; lock?: boolean }>> = {
  kurulum: { hint: 'bağlantılar kurulum sırasıyla · her adım bir kez' },
  'api-kullanimi': { hint: 'bugün, bu ay, kalan ve son 30 gün · kalan, sağlayıcının söylediği kadar' },
  politika: { hint: 'çalışırken değişir · her değişiklik kayıtlı · gevşetmek basılı tut + gerekçe' },
  'sert-sinirlar': { hint: 'kodda · salt okunur · değiştirmek için GitHub PR', lock: true },
  ajanlar: { hint: 'kapatmak tek dokunuş · açmak basılı tut + gerekçe' },
  takvim: { hint: 'config/schedules.yaml · değiştirmek için PR' },
  tercihler: { hint: 'serbest · tek dokunuş, gerekçe yok' },
  veri: { hint: 'saklama süreleri politika · yedek durumu' },
  guvenlik: { hint: 'oturum, denetim zinciri, acil durdurma' },
  hakkinda: { hint: 'sürüm, commit, CI' },
  degisiklikler: { hint: 'kim, ne zaman, gerekçe · silinmez' },
};

function TabContent({ tab, settings }: { tab: SettingsTab; settings: Settings }): ReactNode {
  switch (tab) {
    case 'kurulum':
      return <SetupBody settings={settings} />;
    case 'api-kullanimi':
      return <ApiUsageBody desktop />;
    case 'politika':
      return <PolicyBody settings={settings} desktop />;
    case 'sert-sinirlar':
      return (
        <div className="hard-wrap hard-wrap--desk">
          <HardLimits settings={settings} showSource />
        </div>
      );
    case 'ajanlar':
      return <AgentsBody settings={settings} />;
    case 'takvim':
      return <ScheduleBody settings={settings} />;
    case 'tercihler':
      return <PreferencesBody settings={settings} />;
    case 'veri':
      return <DataBody settings={settings} />;
    case 'guvenlik':
      return <SecurityBody settings={settings} />;
    case 'hakkinda':
      return <AboutBody settings={settings} />;
    case 'degisiklikler':
      return <ChangeLogBody settings={settings} />;
    default:
      return null;
  }
}

/** Sub-pages open inside their tab: Bütçeler and Onay süreleri scroll the Politika tab. */
const ANCHORS: Partial<Record<SettingsTab, string>> = {
  butceler: 'pb-butce',
  'onay-sureleri': 'pb-onay',
  bildirimler: 'pr-bildirim',
  dil: 'pr-dil',
};

export function AyarlarDesktop({ tab, connectionId }: { tab: SettingsTab | null; connectionId: string | null }) {
  const state = usePanel();
  const settings = state.settings;
  const shown = desktopTab(tab);
  useEffect(() => {
    const anchor = tab ? ANCHORS[tab] : undefined;
    if (anchor && settings) document.getElementById(anchor)?.scrollIntoView?.({ block: 'start' });
  }, [tab, settings]);
  if (!settings && shown === 'api-kullanimi') {
    // v0.1.164: API kullanımı has its own read (GET /settings/api-usage) and opens without the settings model too,
    // as on the phone; the tab strip needs the model, so it is left out.
    const head = PANEL_HINTS['api-kullanimi'];
    return (
      <>
        <TopBar title="Ayarlar" subtitle="Kurulum, bağlantılar ve kurallar" status={<TopBarStatus />} />
        <main className="desk-main st-desk">
          <section className="panel st-panel" aria-labelledby="st-panel-h">
            <PanelHead id="st-panel-h" title={TAB_TITLES[shown]} hint={head?.hint ?? ''} />
            <div className="st-panel__body">
              <ApiUsageBody desktop />
            </div>
          </section>
          <ApiUsageRail />
        </main>
      </>
    );
  }
  if (!settings) {
    return (
      <>
        <TopBar title="Ayarlar" subtitle="Kurulum, bağlantılar ve kurallar" status={<TopBarStatus />} />
        <main className="desk-main">
          <SettingsError />
        </main>
      </>
    );
  }
  const summary = setupSummary(settings);
  const dryRun = settings.flags.find((flag) => flag.id === 'executor_dry_run')?.enabled ?? true;
  const selected = connectionId ? settings.connections.find((item) => item.id === connectionId) : undefined;
  const navCurrent = connectionId ? 'baglantilar' : tab === 'degisiklikler' ? null : shown;
  const head = PANEL_HINTS[shown];
  let rail: ReactNode;
  if (selected) rail = <DetailPane connection={selected} settings={settings} now={state.now} />;
  else if (shown === 'kurulum') rail = <ClosedRail settings={settings} />;
  else if (shown === 'api-kullanimi') rail = <ApiUsageRail />;
  else if (['politika', 'sert-sinirlar', 'ajanlar', 'veri', 'degisiklikler'].includes(shown))
    rail = <ChangeLogRail settings={settings} />;
  else rail = <SetupRail settings={settings} summary={summary} />;
  return (
    <>
      <TopBar
        title="Ayarlar"
        subtitle="Kurulum, bağlantılar ve kurallar"
        status={
          <TopBarStatus
            extra={`Kurulum ${summary.ready} / ${summary.total} · Dağıtıcı ${dryRun ? 'kuru çalışmada' : 'canlı'}`}
          />
        }
      />
      <SettingsTabs current={navCurrent} settings={settings} summary={summary} />
      <main className={selected ? 'desk-main st-desk st-desk--pane' : 'desk-main st-desk'}>
        {shown === 'baglantilar' || selected ? (
          <ConnectionsTable settings={settings} selectedId={selected?.id ?? null} />
        ) : (
          <section className="panel st-panel" aria-labelledby="st-panel-h">
            <PanelHead id="st-panel-h" title={TAB_TITLES[shown]} hint={head?.hint ?? ''} lock={head?.lock ?? false} />
            <div className="st-panel__body">
              <TabContent tab={shown} settings={settings} />
            </div>
          </section>
        )}
        {connectionId && !selected ? (
          <aside className="panel st-rail" aria-label="Bağlantı bulunamadı">
            <p className="st-rail__intro">Bu bağlantı yok.</p>
          </aside>
        ) : (
          rail
        )}
      </main>
    </>
  );
}
